import React, { useEffect, useState, useCallback } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

const TUTORIAL_KEY_PREFIX = 'pc_tutorial_seen_v1_'; // per-role (local fallback)

// Steps for agency/superadmin users (desktop)
const AGENCY_STEPS = [
  {
    target: '[data-testid="topnav-home"], [data-tour="home"]',
    title: 'Benvenuto su PostaCentrale 👋',
    content: 'Questo è il menu principale. Da qui accedi a tutte le funzioni: Raccomandate, Clienti Business, Totali e Moduli Bolgetta.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="new-raccomandata"]',
    title: 'Crea una nuova raccomandata',
    content: 'Clicca su "Nuova" in alto a destra (o usa la voce Raccomandate → Nuova) per creare una raccomandata in pochi click. Il codice tracking di PostaCentrale viene generato automaticamente.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="search"]',
    title: 'Cerca raccomandate',
    content: 'Usa "Cerca" in alto per trovare velocemente qualsiasi raccomandata per nome, indirizzo o codice tracking.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="clienti-business"]',
    title: 'Clienti Business',
    content: 'Gestisci i tuoi clienti aziendali e a fine mese genera la pre-fattura PDF mensile.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="totali"]',
    title: 'Totali Giornalieri e Mensili',
    content: 'Visualizza in qualsiasi momento gli incassi del giorno e del mese, e i costi agenzia.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="bolgetta"]',
    title: 'Moduli Bolgetta',
    content: 'Stampa il modulo di Accettazione Bolgetta e la Distinta Analitica Posta Registrata in PDF, pronti per Poste Italiane.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="mobile-link"]',
    title: 'Vista Mobile Portalettere',
    content: 'I tuoi portalettere possono accedere alla vista mobile dedicata da smartphone per consegnare le raccomandate e aggiornare gli esiti.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="help-button"]',
    title: 'Hai bisogno di aiuto?',
    content: 'Premi l\'icona ? in alto in qualsiasi momento per rivedere questo tutorial. Buon lavoro!',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// Lightweight tour for postini (/m page)
const PORTALETTERE_STEPS = [
  {
    target: '[data-testid="m-pickup-button"]',
    title: 'Metti in consegna',
    content: 'Tocca qui per scansionare i codici QR delle raccomandate da caricare in furgone. Si aggiungeranno automaticamente alla tua lista.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-testid="m-today-list"], [data-testid="m-today-empty"]',
    title: 'Da consegnare oggi',
    content: 'Trovi qui tutte le raccomandate che hai preso in carico oggi. Toccale una per una per aggiornarne l\'esito di consegna.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-testid="m-scan-button"], [data-testid="m-code-input"]',
    title: 'Cerca una raccomandata',
    content: 'Se devi consultare una raccomandata specifica puoi cercarla per codice o scansionarne il QR.',
    placement: 'top',
    disableBeacon: true,
  },
];

function getStepsForRole(role) {
  if (role === 'portalettere') return PORTALETTERE_STEPS;
  return AGENCY_STEPS;
}

/**
 * TutorialTour - interactive walkthrough using react-joyride.
 * - Auto-starts on first login (per role).
 * - Can be manually restarted from a "?" Help button (listens to 'pc-start-tutorial' event).
 */
export default function TutorialTour() {
  const { user, refreshMe } = useAuth();
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  const role = user?.role;
  const tutorialKey = role ? `${TUTORIAL_KEY_PREFIX}${role}` : null;
  // Compute steps eagerly from role so they are NEVER empty when joyride mounts.
  const steps = role ? getStepsForRole(role) : [];

  const startTour = useCallback(() => {
    if (!role) return;
    setTourKey(k => k + 1); // force fresh joyride instance
    setRun(true);
    // Persist `tutorial_seen` IMMEDIATELY on tour start. This guarantees the
    // tutorial only appears ONCE per user, even if they close/refresh the
    // browser midway. They can always replay it from the "?" Help button.
    if (tutorialKey) {
      try { localStorage.setItem(tutorialKey, '1'); } catch (e) { /* storage may be blocked (Safari private mode) — non-fatal */ }
    }
    (async () => {
      try {
        await api.post('/auth/me/tutorial-seen');
        if (typeof refreshMe === 'function') {
          await refreshMe();
        }
      } catch (e) { /* non-blocking */ }
    })();
  }, [role, tutorialKey, refreshMe]);

  // Listen to manual trigger from header help button
  useEffect(() => {
    const onTrigger = () => startTour();
    window.addEventListener('pc-start-tutorial', onTrigger);
    return () => window.removeEventListener('pc-start-tutorial', onTrigger);
  }, [startTour]);

  // Auto-start tutorial on first login ever (per user, server-persisted).
  // Falls back to localStorage for backward compatibility, but the server flag
  // is authoritative so the tutorial never shows again after being completed
  // even from a different device or browser.
  useEffect(() => {
    if (!user || !tutorialKey) return;
    if (user.tutorial_seen === true) return; // server says: already done
    const seenLocal = localStorage.getItem(tutorialKey);
    if (seenLocal) return;
    if (run) return; // already running
    const t = setTimeout(() => startTour(), 1200);
    return () => clearTimeout(t);
  }, [user, tutorialKey, startTour, run]);

  // Workaround for react-joyride 3.x: when a step starts with a beacon despite
  // `disableBeacon: true`, auto-click it so the tooltip is shown immediately.
  useEffect(() => {
    if (!run) return;
    let cancelled = false;
    const tryClickBeacon = () => {
      if (cancelled) return;
      const beacon = document.querySelector('.react-joyride__beacon');
      const tooltip = document.querySelector('.react-joyride__tooltip');
      if (beacon && !tooltip) {
        try { beacon.click(); } catch (e) { /* deep-link beacon — non-critical if click fails */ }
      }
    };
    const id1 = setTimeout(tryClickBeacon, 400);
    const id2 = setTimeout(tryClickBeacon, 900);
    const id3 = setTimeout(tryClickBeacon, 1500);
    return () => { cancelled = true; clearTimeout(id1); clearTimeout(id2); clearTimeout(id3); };
  }, [run, tourKey]);

  const handleCallback = (data) => {
    const { status, action } = data;
    // Stop the tour when finished/skipped/closed (tutorial_seen was already
    // persisted on tour start, so nothing extra to do here).
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)
      || action === 'close'
      || action === 'reset') {
      setRun(false);
    }
  };

  if (!user) return null;

  return (
    <Joyride
      key={tourKey}
      steps={steps}
      run={run}
      callback={handleCallback}
      continuous
      showProgress
      showSkipButton
      hideCloseButton={false}
      disableScrolling={false}
      scrollToFirstStep
      scrollOffset={120}
      locale={{
        back: 'Indietro',
        close: 'Chiudi',
        last: 'Termina',
        next: 'Avanti',
        skip: 'Salta tutorial',
      }}
      styles={{
        options: {
          primaryColor: '#f39200', // brand orange
          zIndex: 10000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(15, 15, 20, 0.6)',
          textColor: '#0a0a0a',
        },
        tooltip: {
          borderRadius: 18,
          padding: 18,
          fontSize: 14,
          boxShadow: '0 24px 64px -16px rgba(0,0,0,0.35)',
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 700,
          marginBottom: 6,
        },
        tooltipContent: {
          padding: '4px 0',
          lineHeight: 1.5,
        },
        buttonNext: {
          borderRadius: 999,
          padding: '8px 16px',
          fontWeight: 700,
          fontSize: 13,
        },
        buttonBack: {
          color: '#666',
          fontSize: 13,
          marginRight: 8,
        },
        buttonSkip: {
          color: '#999',
          fontSize: 12,
        },
        spotlight: {
          borderRadius: 14,
        },
      }}
    />
  );
}
